import { useState } from "react";
import axios from "axios";
import { Printer, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ArchiveButton from "./ArchiveButton";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Top-right header action bar for any report page.
 *
 *   [ Print Report ]  [ ⬇ ]  [ 📦 Archive ▾ ]
 *
 * - Print Report → opens the PDF in a new tab and auto-triggers the browser
 *   print dialog (no separate "Download Branded PDF" cruft).
 * - Small download icon → direct save-to-desktop.
 * - Archive → existing ArchiveButton modal.
 *
 * Props mirror ArchiveButton's payload so a single component covers the
 * entire pattern across Tax Prep / CACFP / Income / Expenses / Grant /
 * Equipment / Notes headers.
 */
export default function ReportActions({
  taxYear,
  category,
  source,          // API path that produces the PDF (GET or POST)
  sourceParams = {},
  filenameHint,    // optional: nice download filename (else server header is used)
  titleSuffix = "",
  disabled = false,
  testId,
}) {
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const safeTestId = testId || (category || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-");

  // Build a fetchable URL for the source. Most of our endpoints are GET with
  // ?tax_year=... or ?fiscal_year=...; selected-pdf is the lone POST (used
  // only inside Transactions — not on header bars — so safe to ignore here).
  const buildUrl = () => {
    const params = new URLSearchParams();
    // tax_year is sensible default for most sources
    if (taxYear && !("fiscal_year" in sourceParams) && !source.includes("/notes/")) {
      params.set("tax_year", String(taxYear));
    }
    Object.entries(sourceParams || {}).forEach(([k, v]) => {
      if (v !== null && v !== undefined && !Array.isArray(v)) params.set(k, String(v));
    });
    const qs = params.toString();
    return qs ? `${API}${source.replace(/^\/api/, "")}?${qs}` : `${API}${source.replace(/^\/api/, "")}`;
  };

  const fetchBlob = async () => {
    const r = await axios.get(buildUrl(), { responseType: "blob", withCredentials: true });
    return new Blob([r.data], { type: "application/pdf" });
  };

  const handlePrint = async () => {
    if (disabled || printing) return;
    setPrinting(true);
    try {
      const blob = await fetchBlob();
      const url = window.URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      if (w) {
        // Wait for PDF to load then trigger native print dialog
        w.addEventListener("load", () => { try { w.print(); } catch { /* ignore */ } });
        // Some browsers don't fire load on PDF iframes — fallback timer
        setTimeout(() => { try { w.print(); } catch { /* ignore */ } }, 1200);
      }
      // Revoke after a delay so the new tab can still read it
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error("Print failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setPrinting(false);
    }
  };

  const handleDownload = async () => {
    if (disabled || downloading) return;
    setDownloading(true);
    try {
      const blob = await fetchBlob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameHint || `${(category || "report").toLowerCase().replace(/\s+/g, "-")}-${taxYear || ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      toast.error("Download failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-2" data-testid={`report-actions-${safeTestId}`}>
      <button
        type="button"
        onClick={handlePrint}
        disabled={disabled || printing}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white transition-colors"
        title="Open the print dialog for this report"
        data-testid={`print-report-${safeTestId}`}
      >
        {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
        Print Report
      </button>

      <button
        type="button"
        onClick={handleDownload}
        disabled={disabled || downloading}
        className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors"
        title="Download to your desktop"
        aria-label="Download PDF"
        data-testid={`download-pdf-${safeTestId}`}
      >
        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      </button>

      <ArchiveButton
        taxYear={taxYear}
        category={category}
        source={source}
        sourceParams={sourceParams}
        titleSuffix={titleSuffix}
        size="md"
        disabled={disabled}
        testId={safeTestId}
      />
    </div>
  );
}
